import { Component, Input, forwardRef, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../services/global-environment';
import { Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

interface ApiOption {
  value: string;
  label: string;
}

interface ApiResponse {
  success: boolean;
  data: ApiOption[];
}

@Component({
  selector: 'app-form-dynamic-api-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="form-section">
      <label [attr.for]="id" [class.required-field]="required">{{ label }}</label>
      <select
        [id]="id"
        [name]="name"
        [ngModel]="value"
        (ngModelChange)="onSelectionChange($event)"
        (blur)="onTouched()"
        [disabled]="loading || hasError"
        class="form-input"
      >
        <option value="" disabled>
          {{ loading ? '載入中...' : hasError ? '載入失敗' : placeholder }}
        </option>
        <option *ngFor="let option of options" [value]="option.value">
          {{ option.label }}
        </option>
      </select>
      <div *ngIf="hasError" class="error-message">
        <i class="fas fa-exclamation-triangle"></i>
        載入選項失敗，請稍後再試
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDynamicApiSelectComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormDynamicApiSelectComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '請選擇';
  @Input() required: boolean = false;
  @Input() apiKey: string = '';

  value: any;
  options: ApiOption[] = [];
  loading: boolean = false;
  hasError: boolean = false;
  
  private destroy$ = new Subject<void>();
  private apiUrl = environment.apiUrl;
  private cache: Map<string, ApiOption[]> = new Map();

  // ControlValueAccessor callback 函數
  onChange = (value: any) => {};
  onTouched = () => {};

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    if (this.apiKey) {
      this.loadOptions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 apiKey 變更時，重新載入選項
    if (changes['apiKey'] && !changes['apiKey'].firstChange && changes['apiKey'].currentValue) {
      this.loadOptions();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadOptions(): void {
    if (!this.apiKey) {
      console.warn('FormDynamicApiSelectComponent: apiKey is required');
      return;
    }

    // 檢查快取 - 使用 companyid + apiKey 作為快取鍵
    const companyid = environment.companyid;
    const cacheKey = `${companyid}_${this.apiKey}`;
    
    if (this.cache.has(cacheKey)) {
      this.options = this.cache.get(cacheKey) || [];
      return;
    }

    if (!companyid) {
      console.error('FormDynamicApiSelectComponent: companyid is not available in environment');
      this.hasError = true;
      return;
    }

    this.loading = true;
    this.hasError = false;
    this.options = [];

    const url = `${this.apiUrl}/api/dynamic-data/proxy`;
    const requestBody = {
      companyid: companyid,
      key: this.apiKey
    };
    
    this.http.post<ApiResponse>(url, requestBody)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Failed to load dynamic options:', error);
          this.hasError = true;
          return of({ success: false, data: [] });
        })
      )
      .subscribe(response => {
        this.loading = false;
        
        if (response.success && Array.isArray(response.data)) {
          this.options = response.data;
          // 快取結果 - 使用 companyid + apiKey 作為快取鍵
          this.cache.set(cacheKey, this.options);
        } else {
          this.hasError = true;
          console.error('Invalid API response format:', response);
        }
      });
  }

  onSelectionChange(selectedValue: string): void {
    this.value = selectedValue;
    
    const selectedOption = this.options.find(option => option.value === selectedValue);
    
    if (selectedOption) {
      const optionValue = {
        type: 'dynamic-api-select',
        value: selectedOption.label,
        id: selectedOption.value,
        label: selectedOption.label
      };
      this.onChange(optionValue);
    } else {
      const emptyValue = {
        type: 'dynamic-api-select',
        value: '',
        id: '',
        label: ''
      };
      this.onChange(emptyValue);
    }
  }

  writeValue(value: any): void {
    // 處理新的增強格式
    if (typeof value === 'object' && value !== null && value.hasOwnProperty('type') && value.type === 'dynamic-api-select') {
      this.value = value.id || '';
    } else {
      // 處理舊格式（純字串 ID）
      this.value = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以在這裡處理禁用狀態
  }

  // 重新載入選項（如果需要的話）
  reloadOptions(): void {
    const companyid = environment.companyid;
    const cacheKey = `${companyid}_${this.apiKey}`;
    this.cache.delete(cacheKey);
    this.loadOptions();
  }
}